// Utility functions for analyzing website content

/**
 * Analyze website HTML content for libraries, alerts, ARIA labels, lazy loading, favicon, and form validation
 * @param {string} html - The HTML content of the website
 * @param {Array} libraryRules - The library detection rules from Sanity
 * @returns {Object} - Object containing all detected elements
 */
export const analyzeWebsite = (html, libraryRules) => {
  const detectedLibraries = detectLibraries(html, libraryRules);
  const detectedAlerts = detectJsAlerts(html);
  const detectedAriaLabels = detectAriaLabels(html);
  const detectedLazyLoading = detectLazyLoading(html);
  const detectedFavicon = detectFavicon(html);
  const detectedFormValidation = detectFormValidation(html);
  const detectedMetaTags = detectMetaTags(html);
  const detectedSemanticElements = detectSemanticHTML(html);
  const detectedGridFlexbox = detectGridFlexbox(html);

  return {
    detectedLibraries,
    detectedAlerts,
    detectedAriaLabels,
    detectedLazyLoading,
    detectedFavicon,
    detectedFormValidation,
    detectedMetaTags,
    detectedSemanticElements,
    detectedGridFlexbox
  };
};

/**
 * Detect libraries in HTML content based on library rules
 * @param {string} html - The HTML content
 * @param {Array} libraryRules - The library detection rules
 * @returns {Array} - Array of detected libraries
 */
export const detectLibraries = (html, libraryRules) => {
  return libraryRules.reduce((acc, rule) => {
    const { displayName, keywords, syntaxHighlightType } = rule;
    
    // Split the HTML into lines and check if any keyword appears in a line
    const lines = html
      .split("\n")
      .filter((line) =>
        keywords.some((keyword) =>
          line.toLowerCase().includes(keyword.toLowerCase()),
        ),
      );
      
    if (lines.length > 0) {
      acc.push({
        name: displayName,
        lines,
        syntaxHighlightType,
        count: lines.length
      });
    }
    
    return acc;
  }, []);
};

/**
 * Detect JavaScript alerts in HTML content
 * @param {string} html - The HTML content
 * @returns {Array} - Array of detected alerts
 */
export const detectJsAlerts = (html) => {
  const alertRegex = /alert\s*\([^)]*\)/g;
  const matches = html.match(alertRegex);

  if (matches) {
    return matches.map((match, index) => ({
      id: index,
      name: `JavaScript Alert #${index + 1}`,
      code: match,
      count: 1
    }));
  }
  
  return [];
};

/**
 * Detect ARIA labels and attributes in HTML content
 * @param {string} html - The HTML content
 * @returns {Array} - Array of detected ARIA labels and attributes
 */
export const detectAriaLabels = (html) => {
  const ariaAttributes = [
    'aria-label',
    'aria-labelledby',
    'aria-describedby',
    'aria-description',
    'aria-hidden',
    'aria-live',
    'aria-expanded',
    'aria-controls',
    'aria-selected',
    'aria-required',
    'aria-checked',
    'aria-pressed',
    'aria-current',
    'aria-disabled',
    'aria-invalid',
    'aria-haspopup',
    'role' // Including 'role' as it's a key accessibility attribute
  ];
  
  const lines = html.split('\n');
  const detectedAttributes = [];
  
  lines.forEach((line, lineIndex) => {
    ariaAttributes.forEach(attribute => {
      // Case insensitive search for aria attributes
      const regex = new RegExp(`\\s${attribute}\\s*=\\s*["']([^"']*)["']`, 'i');
      const match = line.match(regex);
      
      if (match) {
        const element = line.trim();
        const elementType = element.match(/<([a-z][a-z0-9]*)/i)?.[1] || 'unknown';
        
        detectedAttributes.push({
          id: detectedAttributes.length,
          attribute: attribute,
          value: match[1],
          element: element,
          elementType: elementType,
          lineNumber: lineIndex + 1,
          name: `${elementType} with ${attribute}="${match[1]}"`
        });
      }
    });
  });
  
  return detectedAttributes;
};

/**
 * Detect lazy loading usage in HTML content
 * @param {string} html - The HTML content
 * @returns {Array} - Array of detected lazy loaded elements
 */
export const detectLazyLoading = (html) => {
  const lazyLoaded = [];
  
  // Modified regex patterns with more flexibility
  const lazyLoadingRegex = /loading\s*=\s*["']lazy["']/i;
  const dataSrcRegex = /data-src\s*=\s*["'][^"']*["']/i;
  const intersectionObserverRegex = /IntersectionObserver/i;
  
  // First try to find all image, iframe, or video tags
  const elementRegex = /<(img|iframe|video)([^>]*)>/gi;
  let elementMatch;
  
  while ((elementMatch = elementRegex.exec(html)) !== null) {
    const fullTag = elementMatch[0];
    const elementType = elementMatch[1];
    const attributes = elementMatch[2];
    
    // Check if this element has lazy loading
    if (lazyLoadingRegex.test(attributes)) {
      lazyLoaded.push({
        id: lazyLoaded.length,
        type: 'native',
        element: fullTag.trim(),
        elementType: elementType,
        lineNumber: getLineNumber(html, elementMatch.index),
        name: `Native lazy loading for <${elementType}>`
      });
    }
    
    // Check for data-src attribute
    if (dataSrcRegex.test(attributes)) {
      lazyLoaded.push({
        id: lazyLoaded.length,
        type: 'data-src',
        element: fullTag.trim(),
        elementType: elementType,
        lineNumber: getLineNumber(html, elementMatch.index),
        name: `data-src lazy loading for <${elementType}>`
      });
    }
  }
  
  // Check for IntersectionObserver usage separately
  const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
  let scriptMatch;
  
  while ((scriptMatch = scriptRegex.exec(html)) !== null) {
    const scriptContent = scriptMatch[1];
    
    if (intersectionObserverRegex.test(scriptContent)) {
      lazyLoaded.push({
        id: lazyLoaded.length,
        type: 'intersection-observer',
        element: scriptMatch[0].substring(0, 100) + '...',  // First 100 chars for brevity
        elementType: 'script',
        lineNumber: getLineNumber(html, scriptMatch.index),
        name: 'IntersectionObserver lazy loading'
      });
    }
  }
  
  return lazyLoaded;
};

// Helper function to get line number from character index
function getLineNumber(html, index) {
  const linesUntilIndex = html.substring(0, index).split('\n');
  return linesUntilIndex.length;
}

/**
 * Detect favicon information in HTML content
 * @param {string} html - The HTML content
 * @returns {Object} - Object containing favicon information
 */
export const detectFavicon = (html) => {
  const faviconInfo = {
    exists: false,
    icons: []
  };
  
  const lines = html.split('\n');
  
  // Regular favicon
  const faviconRegex = /<link[^>]*rel\s*=\s*["'](?:shortcut )?icon["'][^>]*href\s*=\s*["']([^"']*)["'][^>]*>/i;
  
  lines.forEach((line, lineIndex) => {
    // Check for standard favicon
    const faviconMatch = line.match(faviconRegex);
    if (faviconMatch) {
      faviconInfo.exists = true;
      faviconInfo.icons.push({
        id: faviconInfo.icons.length,
        type: 'favicon',
        href: faviconMatch[1],
        element: line.trim(),
        lineNumber: lineIndex + 1,
        name: 'Standard Favicon'
      });
    }
  });
  
  return faviconInfo;
};

/**
 * Detect form validation in HTML content
 * @param {string} html - The HTML content
 * @returns {Object} - Object containing form validation information
 */
export const detectFormValidation = (html) => {
  const formValidation = {
    forms: []
  };
  
  const lines = html.split('\n');
  let currentForm = null;
  let formLineStart = -1;
  
  // Iterate through each line to find forms and their validation attributes
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check for form start
    if (line.match(/<form[^>]*>/i) && !currentForm) {
      const novalidateMatch = line.match(/novalidate/i);
      currentForm = {
        id: formValidation.forms.length,
        hasCustomValidation: novalidateMatch !== null,
        validationElements: [],
        element: line.trim(),
        lineNumber: i + 1
      };
      formLineStart = i;
    }
    
    // If we're inside a form, check for validation attributes
    if (currentForm) {
      // Check for validation attributes
      const requiredMatch = line.match(/<[^>]*\srequired[^>]*>/i);
      const patternMatch = line.match(/<[^>]*\spattern\s*=\s*["'][^"']*["'][^>]*>/i);
      const minlengthMatch = line.match(/<[^>]*\sminlength\s*=\s*["'][^"']*["'][^>]*>/i);
      const maxlengthMatch = line.match(/<[^>]*\smaxlength\s*=\s*["'][^"']*["'][^>]*>/i);
      const minMatch = line.match(/<[^>]*\smin\s*=\s*["'][^"']*["'][^>]*>/i);
      const maxMatch = line.match(/<[^>]*\smax\s*=\s*["'][^"']*["'][^>]*>/i);
      
      // Check for JavaScript validation
      const onInvalidMatch = line.match(/<[^>]*\soninvalid\s*=\s*["'][^"']*["'][^>]*>/i);
      const onSubmitMatch = line.match(/<form[^>]*\sonsubmit\s*=\s*["'][^"']*["'][^>]*>/i);
      
      if (requiredMatch || patternMatch || minlengthMatch || maxlengthMatch || minMatch || maxMatch || onInvalidMatch) {
        let validationType = 'standard';
        if (onInvalidMatch) validationType = 'custom-js';
        
        currentForm.validationElements.push({
          element: line.trim(),
          lineNumber: i + 1,
          type: validationType
        });
      }
      
      if (onSubmitMatch) {
        currentForm.hasCustomValidation = true;
      }
      
      // Check for form end
      if (line.match(/<\/form>/i)) {
        // Get the complete form by joining lines
        currentForm.fullElement = lines.slice(formLineStart, i + 1).join('\n');
        
        // Determine if it's using HTML5 validation or custom validation
        if (currentForm.validationElements.length > 0 && !currentForm.hasCustomValidation) {
          currentForm.validationType = 'html5';
          currentForm.name = 'HTML5 Form Validation';
        } else if (currentForm.hasCustomValidation) {
          currentForm.validationType = 'custom';
          currentForm.name = 'Custom Form Validation';
        } else {
          currentForm.validationType = 'none';
          currentForm.name = 'No Form Validation';
        }
        
        formValidation.forms.push(currentForm);
        currentForm = null;
        formLineStart = -1;
      }
    }
  }
  
  return formValidation;

};

/**
 * Detect meta tags in HTML content
 * @param {string} html - The HTML content
 * @returns {Array} - Array of detected meta tags
 */
export const detectMetaTags = (html) => {
  // Initialize empty array
  const metaTags = [];
  
  const lines = html.split('\n');
  
  // Regex patterns for different types of meta tags
  const metaTagRegex = /<meta[^>]*>/gi;
  const nameAttrRegex = /name\s*=\s*["']([^"']*)["']/i;
  const propertyAttrRegex = /property\s*=\s*["']([^"']*)["']/i;
  const contentAttrRegex = /content\s*=\s*["']([^"']*)["']/i;
  const charsetAttrRegex = /charset\s*=\s*["']([^"']*)["']/i;
  const httpEquivAttrRegex = /http-equiv\s*=\s*["']([^"']*)["']/i;
  
  // Categorize meta tags for easy identification
  const metaCategories = {
    'viewport': 'Viewport',
    'description': 'Description',
    'keywords': 'Keywords',
    'author': 'Author',
    'robots': 'Robots',
    'og:': 'Open Graph',
    'twitter:': 'Twitter Card',
    'charset': 'Character Set',
    'http-equiv': 'HTTP-Equiv'
  };
  
  lines.forEach((line, lineIndex) => {
    // Find all meta tags in the current line
    const metaMatches = [...line.matchAll(metaTagRegex)];
    
    if (metaMatches.length > 0) {
      metaMatches.forEach(match => {
        const metaTag = match[0];
        
        // Extract attributes
        const nameMatch = metaTag.match(nameAttrRegex);
        const propertyMatch = metaTag.match(propertyAttrRegex);
        const contentMatch = metaTag.match(contentAttrRegex);
        const charsetMatch = metaTag.match(charsetAttrRegex);
        const httpEquivMatch = metaTag.match(httpEquivAttrRegex);
        
        let tagName = '';
        let tagType = 'standard';
        let tagContent = contentMatch ? contentMatch[1] : '';
        
        // Determine tag type and name
        if (nameMatch) {
          tagName = nameMatch[1];
          
          // Categorize based on common meta tag names
          for (const [key, value] of Object.entries(metaCategories)) {
            if (tagName.toLowerCase().includes(key.toLowerCase())) {
              tagType = value;
              break;
            }
          }
        } else if (propertyMatch) {
          tagName = propertyMatch[1];
          
          // Categorize based on property prefixes
          for (const [key, value] of Object.entries(metaCategories)) {
            if (tagName.toLowerCase().includes(key.toLowerCase())) {
              tagType = value;
              break;
            }
          }
        } else if (charsetMatch) {
          tagName = 'charset';
          tagContent = charsetMatch[1];
          tagType = 'Character Set';
        } else if (httpEquivMatch) {
          tagName = httpEquivMatch[1];
          tagType = 'HTTP-Equiv';
        }
        
        // Add to array
        metaTags.push({
          id: metaTags.length,
          name: tagName || 'Unnamed Meta Tag',
          type: tagType,
          content: tagContent,
          element: metaTag.trim(),
          lineNumber: lineIndex + 1
        });
      });
    }
  });
  
  return metaTags;
};

/**
 * Analyzes HTML content for semantic HTML elements
 * @param {string} html - The HTML content to analyze
 * @returns {Array} - Array of detected semantic HTML elements
 */
export function detectSemanticHTML(html) {
  
  if (!html || typeof html !== 'string') {
    console.warn('No valid HTML provided for semantic analysis');
    return [];
  }

  // List of semantic HTML tags to detect
  const semanticTags = [
    'article', 'aside', 'details', 'figcaption', 'figure', 
    'footer', 'header', 'main', 'mark', 'nav', 
    'section', 'summary', 'time', 'address', 'output',
    'progress', 'meter', 'ruby', 'rt', 'rp'
  ];

  // Create a temporary DOM parser
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  const semanticElements = [];
  let elementId = 1;

  // Function to get line number estimate (approximate based on index in HTML string)
  const getLineNumber = (element) => {
    try {
      // Get the HTML representation of the element
      const elementHtml = element.outerHTML;
      // Find the position in the original HTML
      const position = html.indexOf(elementHtml);
      if (position === -1) return 'Unknown';
      
      // Count newlines up to this position
      const upToPosition = html.substring(0, position);
      const lineNumber = upToPosition.split('\n').length;
      return lineNumber;
    } catch (error) {
      console.error('Error determining line number:', error);
      return 'Unknown';
    }
  };

  // Process each semantic tag type
  semanticTags.forEach(tagName => {
    const elements = doc.getElementsByTagName(tagName);
    
    Array.from(elements).forEach(element => {
      // Get element attributes
      const attributes = {};
      Array.from(element.attributes).forEach(attr => {
        attributes[attr.name] = attr.value;
      });
      
      // Get text content, trimmed and limited
      const content = element.textContent?.trim().substring(0, 100) || '';
      
      // Create a serialized version of just this element
      const elementHTML = element.outerHTML;
      
      semanticElements.push({
        id: elementId++,
        tagName: element.tagName,
        element: elementHTML,
        attributes,
        content,
        lineNumber: getLineNumber(element)
      });
    });
  });

  return semanticElements;
}

// Utility functions for analyzing website content

/**
 * Detect CSS Grid and Flexbox usage in website content
 * @param {string} html - The HTML content of the website
 * @returns {Array} - Array of detected Grid and Flexbox usage
 */
export const detectGridFlexbox = (html) => {
  const gridFlexboxItems = [];
  
  if (!html || typeof html !== 'string') {
    console.warn('No valid HTML provided for Grid/Flexbox analysis');
    return [];
  }
 
  // CSS properties to look for
  const gridProperties = [
    'display:\\s*grid',
    'grid-template',
    'grid-template-columns',
    'grid-template-rows',
    'grid-template-areas',
    'grid-column',
    'grid-row',
    'grid-area',
    'grid-gap',
    'gap',
    'grid-column-gap',
    'grid-row-gap'
  ];
  
  const flexboxProperties = [
    'display:\\s*flex',
    'flex-direction',
    'flex-wrap',
    'flex-flow',
    'justify-content',
    'align-items',
    'align-content',
    'flex-grow',
    'flex-shrink',
    'flex-basis',
    'flex:'
  ];

  // Process inline styles in HTML tags
  // This approach finds style attributes in HTML elements
  let itemId = 1;
  
  // Detect inline styles
  const styleAttrRegex = /<[^>]*style\s*=\s*["']([^"']*)["'][^>]*>/gi;
  let match;
  
  while ((match = styleAttrRegex.exec(html)) !== null) {
    const styleContent = match[1];
    const fullElement = match[0].trim();
    const lineNumber = getLineNumber(html, match.index);
    const elementType = fullElement.match(/<([a-z][a-z0-9]*)/i)?.[1] || 'unknown';
    
    // Check for grid properties
    const hasGrid = gridProperties.some(prop => {
      const regex = new RegExp(prop, 'i');
      return regex.test(styleContent);
    });
    
    // Check for flexbox properties
    const hasFlexbox = flexboxProperties.some(prop => {
      const regex = new RegExp(prop, 'i');
      return regex.test(styleContent);
    });
    
    if (hasGrid || hasFlexbox) {
      const type = hasGrid ? (hasFlexbox ? 'grid+flexbox' : 'grid') : 'flexbox';
      
      gridFlexboxItems.push({
        id: itemId++,
        type,
        implementation: 'inline',
        element: fullElement,
        elementType,
        lineNumber,
        styles: styleContent,
        name: `Inline ${type} on <${elementType}>`
      });
    }
  }
  
  // Detect in <style> tags
  const styleTagRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let styleMatch;
  
  while ((styleMatch = styleTagRegex.exec(html)) !== null) {
    const styleContent = styleMatch[1];
    const styleLineStart = getLineNumber(html, styleMatch.index);
    
    // Look for CSS rules with grid or flexbox properties
    const cssRuleRegex = /([^{}]+)\{([^{}]*)\}/g;
    let ruleMatch;
    
    while ((ruleMatch = cssRuleRegex.exec(styleContent)) !== null) {
      const selector = ruleMatch[1].trim();
      const declarations = ruleMatch[2];
      
      // Check for grid properties
      const hasGrid = gridProperties.some(prop => {
        const regex = new RegExp(prop, 'i');
        return regex.test(declarations);
      });
      
      // Check for flexbox properties
      const hasFlexbox = flexboxProperties.some(prop => {
        const regex = new RegExp(prop, 'i');
        return regex.test(declarations);
      });
      
      if (hasGrid || hasFlexbox) {
        const type = hasGrid ? (hasFlexbox ? 'grid+flexbox' : 'grid') : 'flexbox';
        
        // Calculate approximate line number within the style block
        const selectorPosition = styleContent.indexOf(ruleMatch[0]);
        const selectorLines = styleContent.substring(0, selectorPosition).split('\n').length - 1;
        const approxLineNumber = styleLineStart + selectorLines;
        
        gridFlexboxItems.push({
          id: itemId++,
          type,
          implementation: 'internal-css',
          selector,
          styles: declarations,
          lineNumber: approxLineNumber,
          name: `${type.charAt(0).toUpperCase() + type.slice(1)} in <style> (${selector})`
        });
      }
    }
  }
  
  // Look for classes that might use grid or flexbox
  // This is more speculative since we don't have access to external CSS
  const classRegex = /<[^>]*class\s*=\s*["']([^"']*)["'][^>]*>/gi;
  let classMatch;
  
  while ((classMatch = classRegex.exec(html)) !== null) {
    const classesStr = classMatch[1];
    const fullElement = classMatch[0].trim();
    const lineNumber = getLineNumber(html, classMatch.index);
    const elementType = fullElement.match(/<([a-z][a-z0-9]*)/i)?.[1] || 'unknown';
    const classes = classesStr.split(/\s+/);
    
    // Look for common grid/flexbox naming patterns in classes
    const gridClasses = classes.filter(cls => 
      /grid|row|col-|column|area|template|gap/.test(cls.toLowerCase())
    );
    
    const flexClasses = classes.filter(cls => 
      /flex|justify|align|wrap|shrink|grow/.test(cls.toLowerCase())
    );
    
    if (gridClasses.length > 0 || flexClasses.length > 0) {
      const type = gridClasses.length > 0 ? (flexClasses.length > 0 ? 'grid+flexbox' : 'grid') : 'flexbox';
      const relevantClasses = [...new Set([...gridClasses, ...flexClasses])].join(' ');
      
      gridFlexboxItems.push({
        id: itemId++,
        type,
        implementation: 'class-based',
        element: fullElement,
        elementType,
        classes: relevantClasses,
        lineNumber,
        name: `Potential ${type} classes on <${elementType}>`
      });
    }
  }
  
  return gridFlexboxItems;
};